import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowLeft, Filter, Award, Loader2 } from "lucide-react";
import Seo from "../components/Seo";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const US_STATES = [
  "", "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

/**
 * LeaderboardPage — public, opt-in "top-preserved packs" leaderboard.
 *
 * Every entry was published by an owner who explicitly toggled
 * "Share to leaderboard" on their EVIQ Battery Passport™. The rows are
 * anonymized — only VIN suffix (last 8), model, year, trim, state and
 * the Trust Score / Health Grade are exposed.
 */
export default function LeaderboardPage() {
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [state, setState] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (model) params.set("model", model);
    if (year) params.set("year", year);
    if (state) params.set("state", state);
    params.set("limit", "50");
    setLoading(true);
    fetch(`${API_BASE}/api/leaderboard?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : { rows: [] }))
      .then((d) => setRows(d.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [model, year, state]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="leaderboard-page">
      <Seo
        title="Tesla Battery Leaderboard — best-preserved packs on the road"
        description="Opt-in public rankings of Tesla owners with the highest Trust Scores and most preserved battery health, ranked by EVIQ signed Battery Passports. Filter by model, year, and state."
        canonical="https://eviq.pro/leaderboard"
      />
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link
          to="/"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5 mb-6"
          data-testid="leaderboard-back"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={2.4} />
          EVIQ
        </Link>

        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2 inline-flex items-center gap-2">
            <Trophy className="w-3 h-3" style={{ color: "#FFB020" }} strokeWidth={2.4} />
            EVIQ Leaderboard
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold leading-tight">
            The <span className="brand-text-gradient">best-preserved packs</span> on the road.
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mt-3 max-w-xl">
            Opt-in rankings of Tesla owners who chose to share their signed
            EVIQ Battery Passport™ publicly. Anonymized — VIN suffix only. Filter
            by model, year, or state.
          </p>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="leaderboard-filters">
          <Filter className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2.4} />
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            data-testid="leaderboard-filter-model"
            className="rounded-md border border-white/10 bg-black/60 text-[12px] text-white px-2 py-1.5 focus:outline-none focus:border-[#39FF14]/50"
          >
            <option value="">All models</option>
            <option>Model 3</option>
            <option>Model Y</option>
            <option>Model S</option>
            <option>Model X</option>
            <option>Cybertruck</option>
          </select>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            data-testid="leaderboard-filter-year"
            className="rounded-md border border-white/10 bg-black/60 text-[12px] text-white px-2 py-1.5 focus:outline-none focus:border-[#39FF14]/50"
          >
            <option value="">All years</option>
            {Array.from({ length: 12 }).map((_, i) => {
              const y = new Date().getFullYear() - i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            data-testid="leaderboard-filter-state"
            className="rounded-md border border-white/10 bg-black/60 text-[12px] text-white px-2 py-1.5 focus:outline-none focus:border-[#39FF14]/50"
          >
            {US_STATES.map((s) => (
              <option key={s || "any"} value={s}>{s || "All states"}</option>
            ))}
          </select>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-zinc-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.2} />
            Loading ranks…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-zinc-400 text-sm" data-testid="leaderboard-empty">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] mb-2" style={{ color: "#39FF14" }}>
              Opening with the founding cohort
            </div>
            <div className="text-white font-semibold mb-1">The first ranked packs are being onboarded now.</div>
            Publish your passport and toggle{" "}
            <span className="text-white font-semibold">Share to leaderboard</span> — early
            entries claim the top spots before the crowd arrives.
          </div>
        ) : (
          <ol className="space-y-2" data-testid="leaderboard-rows">
            {rows.map((r, i) => (
              <li
                key={r.passport_id}
                className="rounded-xl border p-3 flex items-center gap-3"
                style={{
                  borderColor: i < 3 ? "rgba(255,176,32,0.35)" : "rgba(255,255,255,0.08)",
                  background: i < 3 ? "rgba(255,176,32,0.05)" : "rgba(255,255,255,0.02)",
                }}
                data-testid={`leaderboard-row-${i}`}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center border shrink-0 font-display text-sm font-semibold"
                  style={{
                    borderColor: i < 3 ? "rgba(255,176,32,0.6)" : "rgba(255,255,255,0.15)",
                    background: i < 3 ? "rgba(255,176,32,0.12)" : "rgba(255,255,255,0.04)",
                    color: i < 3 ? "#FFB020" : "#ffffff",
                  }}
                >
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-white font-semibold leading-tight flex items-center gap-1.5">
                    {r.year} {r.model}{r.trim ? ` · ${r.trim}` : ""}
                    {r.legendary && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded px-1 py-[1px] font-mono text-[9px] uppercase tracking-wider"
                        style={{ background: "rgba(255,176,32,0.15)", color: "#FFB020", border: "1px solid rgba(255,176,32,0.35)" }}
                        title="Legendary Preservation™ — Trust Score 95+ for 90 days"
                        data-testid={`leaderboard-row-${i}-legendary`}
                      >
                        <Trophy className="w-2.5 h-2.5" strokeWidth={2.6} />
                        Legendary
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
                    VIN …{r.vin_last8}{r.state ? ` · ${r.state}` : ""}
                    {" · "}SoH {r.soh_pct}%
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-1 justify-end">
                    <Award className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={2.6} />
                    <span className="font-display text-lg font-semibold text-white leading-none">
                      {r.trust_score ?? r.eviq_score}
                    </span>
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
                    {r.health_grade || (r.trust_score ?? r.eviq_score) + "/100"}
                  </div>
                </div>
                <Link
                  to={`/passport/${r.passport_id}`}
                  data-testid={`leaderboard-row-${i}-view`}
                  className="shrink-0 rounded-full border border-white/15 px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-white/[0.06]"
                >
                  View
                </Link>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
